import React, { useMemo } from "react";

import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { useFieldArray, useForm } from "react-hook-form";
import { v4 } from "uuid";

import { FieldErrors } from "src/api/types";
import { AssignmentRow } from "src/assignmentNode/AssignmentRow";
import { Card } from "src/base-components/Card";
import { Effect } from "src/clients/flow-api";
import {
  AssignmentNode,
  AssignmentNodeData,
} from "src/constants/NodeDataTypes";
import { Button } from "src/design-system/Button";
import { useFormApplyNodeSuggestion } from "src/nodeEditor/applyNodeSuggestion";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import {
  convertFieldErrorsBeToFe,
  mergeFieldIdAndName,
  RunErrorFieldNames,
} from "src/utils/FieldErrorUtils";
import { useSubmitForm } from "src/utils/useSubmitForm";

type AssignmentNodeForm = {
  default_effects: Effect[];
};

const usePreviousValues = (
  nodeEffects?: AssignmentNodeData["default_effects"],
) => {
  const defaultEffects = useMemo(
    () => [{ id: v4(), value: "", target: "" }],
    [],
  );

  return useMemo(
    () => ({
      default_effects: nodeEffects?.length ? nodeEffects : defaultEffects,
    }),
    [nodeEffects, defaultEffects],
  );
};

type Props = {
  selectedNode: AssignmentNode;
  lazyInputs: boolean;
} & NodeEditorBaseProps<AssignmentNodeData>;

export const AssignmentNodeEditor: React.FC<Props> = ({
  selectedNode,
  immutable,
  lazyInputs,
  isReactive,
  onUpdate,
  displayedError,
  hideActionButtons,
}) => {
  const previousValues = usePreviousValues(selectedNode.data.default_effects);

  const formMethods = useForm<AssignmentNodeForm>({
    defaultValues: previousValues,
    ...(isReactive && { values: previousValues }),
  });
  const { control, register, getValues } = formMethods;
  useFormApplyNodeSuggestion<AssignmentNodeData>(
    selectedNode.id,
    (suggestion) => {
      formMethods.setValue("default_effects", suggestion.default_effects);
    },
  );

  useSubmitForm({
    onChange: (data: AssignmentNodeForm) => {
      onUpdate({ newData: data });
    },
    disabled: isReactive,
    previousValues: previousValues,
    subscribe: formMethods.subscribe,
  });

  const { fields, append, remove, move } = useFieldArray({
    control: control,
    name: "default_effects",
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const fieldToMove = fields.findIndex((field) => field.id === active.id);
      const fieldToInsert = fields.findIndex((field) => field.id === over.id);
      if (fieldToMove !== -1 && fieldToInsert !== -1) {
        move(fieldToMove, fieldToInsert);
      }
    }
  };
  const fieldErrors: FieldErrors | undefined = displayedError?.field_errors
    ? convertFieldErrorsBeToFe(displayedError.field_errors, true)
    : undefined;

  return (
    <Card className="w-full space-y-3">
      <span className="font-inter-medium-12px text-gray-500">Set value of</span>
      <div className="h-px rounded-full bg-gray-200" />
      <div className="space-y-3">
        <DndContext onDragEnd={handleDragEnd}>
          <SortableContext
            disabled={immutable}
            items={fields.map((row) => row.id)}
          >
            {fields.map((field, index) => (
              <AssignmentRow
                key={field.id}
                control={control}
                fieldErrorLeft={
                  fieldErrors?.[
                    mergeFieldIdAndName(
                      getValues().default_effects?.at(index)?.id,
                      RunErrorFieldNames.LEFT,
                    )
                  ]
                }
                fieldErrorRight={
                  fieldErrors?.[
                    mergeFieldIdAndName(
                      getValues().default_effects?.at(index)?.id,
                      RunErrorFieldNames.RIGHT,
                    )
                  ]
                }
                hideActionButtons={hideActionButtons}
                id={field.id}
                immutable={immutable}
                index={index}
                lazyInputs={lazyInputs}
                register={register}
                searchHighlightKey={
                  getValues().default_effects?.at(index)?.id ?? ""
                }
                onRemove={fields.length > 1 ? () => remove(index) : undefined}
              />
            ))}
          </SortableContext>
        </DndContext>
        {!hideActionButtons && (
          <Button
            dataLoc="assignment-node-add-assignment"
            isDisabled={immutable}
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={() => append({ id: v4(), value: "", target: "" })}
          >
            Add field
          </Button>
        )}
      </div>
    </Card>
  );
};
